import type { Dispatch, SetStateAction } from 'react';
import type { ApplePayElementProps } from '@adyen/adyen-web/dist/types/components/ApplePay/types';
import type { Cart } from '../../../codegen/types';
import type { PaymentContextType } from '../../../context/paymentContext';
import type { PlaceOrderMutationFn } from '../../../types';
import { PaymentMethodTypes } from '../../../utils/adyen/adyen';
import { isStar2Check } from '../../../utils/globalEUtils/isStar2Check';
import { sendAddPaymentInfoEvent } from '../../../utils/gtm/events/add_payment_info/sendAddPaymentInfoEvent';
import { newRelicAvailabilityChecker } from '../../../utils/newrelic';
import { setItem } from '../../../utils/storage';

// Adyen docs - web components - Apple Pay
// https://docs.adyen.com/payment-methods/apple-pay/web-component/

export interface CreateApplePayConfigProps {
  locale: string;
  paymentData: PaymentContextType['paymentData'];
  miniCart: Cart | Record<string, never>;
  cartId: string;
  baseUrl: string;
  setIsPaymentBtnDisabled: Dispatch<SetStateAction<boolean>>;
  setPaymentInProgress: Dispatch<SetStateAction<boolean>>;
  setPaymentError: Dispatch<SetStateAction<boolean>>;
  validateDiscountsBeforePayment: () => Promise<boolean>;
  placeOrderMutation: PlaceOrderMutationFn;
  onBeforeSubmit: (data: unknown, actions: unknown) => Promise<void>;
}

export const createApplePayConfig = ({
  locale,
  paymentData,
  miniCart,
  cartId,
  baseUrl,
  setIsPaymentBtnDisabled,
  setPaymentInProgress,
  setPaymentError,
  validateDiscountsBeforePayment,
  placeOrderMutation,
  onBeforeSubmit,
}: CreateApplePayConfigProps): Partial<ApplePayElementProps> => {
  const isStar2 = isStar2Check({ locale });
  const merchantAccount = paymentData?.session?.merchantAccount;
  const merchantId = paymentData?.paymentMethods?.find(({ type }) => type === PaymentMethodTypes.APPLE_PAY)
    ?.configuration?.merchantId;

  return {
    // TODO: The countryCode is required by default for Apple Pay - confirm if it's necessary
    showPayButton: true,
    buttonType: 'check-out',
    buttonColor: 'black',
    ...(isStar2 &&
      merchantAccount &&
      merchantAccount?.length > 64 && {
        configuration: {
          merchantName: 'Clarks',
          merchantId: merchantId ?? '',
        },
      }),
    beforeSubmit: async (data, _, actions) => {
      try {
        setIsPaymentBtnDisabled(true);
        setPaymentInProgress(true);

        const isDiscountValid = await validateDiscountsBeforePayment();

        if (!isDiscountValid) {
          actions.reject();

          setPaymentInProgress(false);
          setIsPaymentBtnDisabled(false);

          return;
        }

        const result = await placeOrderMutation();

        if (result?.data?.placeOrder.accepted) {
          sendAddPaymentInfoEvent(miniCart, PaymentMethodTypes.APPLE_PAY, cartId, baseUrl);
          setItem('CUSTOMER_PAYMENT_METHOD_NAME', 'Apple Pay', true, 'localStorage');
          setItem('CUSTOMER_PAYMENT_METHOD_TYPE', PaymentMethodTypes.APPLE_PAY, true, 'localStorage');

          await onBeforeSubmit(data, actions);
        } else {
          actions.reject();
          setPaymentError(true);
          setIsPaymentBtnDisabled(false);
          setPaymentInProgress(false);
        }
      } catch (error) {
        actions.reject();
        setPaymentError(true);
        setIsPaymentBtnDisabled(false);
        setPaymentInProgress(false);
        console.error('Error during ApplePay payment processing:', error);
        newRelicAvailabilityChecker(() => {
          window.newrelic.noticeError(new Error(`Error during ApplePay payment processing: ${error}`));
        });
      }
    },
  };
};
